// P2b tanda 7 (PARTE A) — sección "Productos relacionados" ("Combínalo con").
// Buscador por nombre o SKU con miniatura/precio/estado; hasta 3 elegidos como
// tarjetas con orden por arrastre y quitar; fila "Sugeridos" (solo sugerencias:
// nada se guarda sin elegirlo). Un elegido en borrador/papelera lleva el aviso
// "no se muestra en la tienda hasta que se publique". El valor son IDS
// (cross_sell_ids); el padre decide cuándo viajan (regla de solo tocados).
import React, { useState, useMemo } from 'react';
import { Plus, Trash2, Search } from 'lucide-react';
import ImagenProducto from './ImagenProducto';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import { sugerirRelacionados } from '../utils/relacionados';
import { decodeHtml } from '../utils/decodeHtml';
import './ProductosRelacionados.css';

const MAX = 3;
const normal = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const precioDe = (p) => {
  const n = parseFloat(p.price || p.regular_price);
  return Number.isFinite(n) ? `$${n.toLocaleString('es-CO')}` : '—';
};
const ESTADO = { publish: 'Publicado', draft: 'Borrador', private: 'Privado', pending: 'Pendiente' };

function TarjetaElegido({ p, onQuitar }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = { transform: DndCSS.Transform.toString(transform), transition };
  const sinPublicar = p.status && p.status !== 'publish';
  return (
    <div ref={setNodeRef} style={style} className={`pr-elegido${isDragging ? ' pr-elegido--drag' : ''}`}>
      {p.image ? <ImagenProducto src={p.image} srcset={p.imageSrcset} sizes="miniatura" phWidth={40} {...attributes} {...listeners} /> : <span className="pr-mini pr-mini--vacia" {...attributes} {...listeners}>—</span>}
      <div className="pr-elegido-info">
        <span className="pr-nombre">{decodeHtml(p.name)}</span>
        <span className="pr-precio">{precioDe(p)}</span>
        {sinPublicar && <span className="pr-aviso">no se muestra en la tienda hasta que se publique</span>}
      </div>
      <button type="button" className="pp-viconbtn pp-viconbtn--danger" title="Quitar"
        aria-label={`Quitar ${decodeHtml(p.name)}`} onClick={() => onQuitar(p.id)}>
        <Trash2 size={13} aria-hidden="true" />
      </button>
    </div>
  );
}

function FilaCandidato({ p, onAgregar, deshabilitado }) {
  return (
    <div className="pr-candidato">
      {p.image ? <ImagenProducto src={p.image} srcset={p.imageSrcset} sizes="miniatura" phWidth={40} /> : <span className="pr-mini pr-mini--vacia">—</span>}
      <span className="pr-nombre">{decodeHtml(p.name)}</span>
      <span className="pr-precio">{precioDe(p)}</span>
      <span className={`pr-estado${p.status !== 'publish' ? ' pr-estado--off' : ''}`}>{ESTADO[p.status] || p.status}</span>
      <button type="button" className="pp-vlink" disabled={deshabilitado}
        aria-label={`Agregar ${decodeHtml(p.name)}`} onClick={() => onAgregar(p.id)}>
        <Plus size={13} aria-hidden="true" /> Agregar
      </button>
    </div>
  );
}

export default function ProductosRelacionados({ value = [], onChange, productos = [], productoId = null, categorias = [], categoryIds = [] }) {
  const [busqueda, setBusqueda] = useState('');
  const porId = useMemo(() => new Map((productos || []).map((p) => [Number(p.id), p])), [productos]);
  const elegidos = (value || []).map((id) => porId.get(Number(id)) || { id, name: `#${id}`, status: 'publish' });
  const lleno = (value || []).length >= MAX;

  const resultados = useMemo(() => {
    const q = normal(busqueda.trim());
    if (!q) return [];
    return (productos || [])
      .filter((p) => Number(p.id) !== Number(productoId) && !(value || []).map(Number).includes(Number(p.id)))
      .filter((p) => normal(p.name).includes(q) || normal(p.sku).includes(q))
      .slice(0, 8);
  }, [busqueda, productos, value, productoId]);

  const sugeridos = useMemo(
    () => sugerirRelacionados({ categoryIds, productoId, elegidos: value }, productos, categorias),
    [categoryIds, productoId, value, productos, categorias]
  );

  const agregar = (id) => {
    if (lleno || (value || []).map(Number).includes(Number(id))) return;
    onChange([...(value || []), Number(id)]);
    setBusqueda('');
  };
  const quitar = (id) => onChange((value || []).filter((x) => Number(x) !== Number(id)));
  const sensores = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = (value || []).map(Number);
    const from = ids.indexOf(Number(active.id));
    const to = ids.indexOf(Number(over.id));
    if (from >= 0 && to >= 0) onChange(arrayMove(ids, from, to));
  };

  return (
    <div className="pr" data-foco="relacionados" tabIndex={-1}>
      <p className="pp-help">
        Aparecen en la ficha como "Combínalo con". Elige 2 o 3 que de verdad se usen juntos:
        un body con su falda, un rubor con su brocha.
      </p>

      {elegidos.length > 0 && (
        <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={(value || []).map(Number)} strategy={rectSortingStrategy}>
            <div className="pr-elegidos">
              {elegidos.map((p) => <TarjetaElegido key={p.id} p={p} onQuitar={quitar} />)}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className="pr-buscar">
        <Search size={14} aria-hidden="true" />
        <input className="pp-input" value={busqueda} placeholder={lleno ? 'Ya elegiste 3 (quita uno para cambiar)' : 'Buscar por nombre o SKU…'}
          aria-label="Buscar producto relacionado" disabled={lleno}
          onChange={(e) => setBusqueda(e.target.value)} />
      </div>
      {resultados.length > 0 && (
        <div className="pr-resultados">
          {resultados.map((p) => <FilaCandidato key={p.id} p={p} onAgregar={agregar} deshabilitado={lleno} />)}
        </div>
      )}

      {sugeridos.length > 0 && (
        <div className="pr-sugeridos">
          <span className="pr-sugeridos-titulo">Sugeridos</span>
          {sugeridos.map((p) => <FilaCandidato key={p.id} p={p} onAgregar={agregar} deshabilitado={lleno} />)}
        </div>
      )}
    </div>
  );
}
